import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar as CalendarIcon, Clock, Users, List,
  Plus, Activity, Target, Bot,
  Edit3, Trash2, Check, AlertCircle, Loader2, MapPin,
} from 'lucide-react';
import { useCalendarEvents } from '../../hooks/useCalendarEvents';
import { useToast } from '../../contexts/ToastContext';
import EventModal from './EventModal';
import {
  getTypeMeta, getStatusMeta, getColorClass, getPriorityColor, getPriorityLabel,
  formatTimeRange, formatLongDate, formatMonthYear,
  isSameDay, startOfWeek, addDays,
} from './calendarUtils';

/* ---------- Carte d'événement réutilisable ---------- */
function EventCard({ event, index, onEdit, onDelete, onComplete }) {
  const type = getTypeMeta(event.type);
  const status = getStatusMeta(event.status);
  const Icon = type.icon;
  const attendeesCount = event.attendees?.length || 0;
  const isAgent = event.source === 'agent';

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className="card p-4 sm:p-5"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div className="flex items-start gap-4 min-w-0 flex-1">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}
          >
            <Icon size={20} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} strokeWidth={1.5} />
          </div>

          <div className="space-y-1.5 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-ink font-semibold">
                {event.title}
              </h4>
              {isAgent && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-accent-green/15 text-accent-green border border-accent-green/30">
                  <Bot size={12} /> Créé par l'IA
                </span>
              )}
            </div>
            {event.description && <p className="text-label text-sm">{event.description}</p>}

            <div className="flex items-center gap-4 text-sm flex-wrap">
              <div className="flex items-center gap-1.5 text-label">
                <Clock size={14} />
                <span>{formatTimeRange(event.startDate, event.endDate)}</span>
              </div>
              {attendeesCount > 0 && (
                <div className="flex items-center gap-1.5 text-label">
                  <Users size={14} />
                  <span>{attendeesCount} participant{attendeesCount > 1 ? 's' : ''}</span>
                </div>
              )}
              {event.location && (
                <div className="flex items-center gap-1.5 text-label">
                  <MapPin size={14} />
                  <span>{event.location}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap pl-14 sm:pl-0">
          <motion.span
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(event.priority)}`}
            whileHover={{ scale: 1.05 }}
          >
            {getPriorityLabel(event.priority)}
          </motion.span>

          <div className="flex items-center gap-1">
            {event.status === 'scheduled' && (
              <motion.button
                onClick={() => onComplete(event)}
                title="Marquer comme terminé"
                className="p-2 rounded-lg hover:bg-accent-green/10 text-label hover:text-accent-green transition-all"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Check size={16} />
              </motion.button>
            )}
            <motion.button
              onClick={() => onEdit(event)}
              title="Modifier"
              className="p-2 rounded-lg text-label hover:text-ink transition-all hover:bg-[var(--c-hover)]"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Edit3 size={16} />
            </motion.button>
            <motion.button
              onClick={() => onDelete(event)}
              title="Supprimer"
              className="p-2 rounded-lg hover:bg-red-500/10 text-label hover:text-red-400 transition-all"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Trash2 size={16} />
            </motion.button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3" style={{ borderTop: '0.5px solid var(--c-border)' }}>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${status.dot} ${event.status === 'scheduled' ? 'animate-pulse' : ''}`} />
          <span className={`text-xs ${status.text}`}>{status.label}</span>
        </div>
        <span className="px-2.5 py-1 rounded-lg text-xs font-medium text-accent-green" style={{ background: 'rgba(var(--c-accent-green-rgb), 0.1)' }}>
          {type.label}
        </span>
      </div>
    </motion.div>
  );
}

/* ---------- Page ---------- */
export default function Calendrier() {
  const [currentView, setCurrentView] = useState('semaine');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const today = useMemo(() => new Date(), []);

  const { events, loading, error, createEvent, updateEvent, deleteEvent, updateStatus } =
    useCalendarEvents();
  const toast = useToast();

  const views = [
    { id: 'jour', label: 'Jour', icon: Clock },
    { id: 'semaine', label: 'Semaine', icon: CalendarIcon },
    { id: 'mois', label: 'Mois', icon: Target },
    { id: 'agenda', label: 'Agenda', icon: List },
  ];

  /* --- Sous-ensembles par vue --- */
  const dayEvents = useMemo(
    () => events.filter((e) => isSameDay(e.startDate, today)),
    [events, today]
  );

  const weekStart = useMemo(() => startOfWeek(today), [today]);
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  const weekEvents = useMemo(() => {
    const end = addDays(weekStart, 7);
    return events.filter((e) => {
      const d = new Date(e.startDate);
      return d >= weekStart && d < end;
    });
  }, [events, weekStart]);

  const monthGrid = useMemo(() => {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const leading = (first.getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < leading; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(today.getFullYear(), today.getMonth(), d);
      cells.push({
        date,
        day: d,
        isToday: isSameDay(date, today),
        count: events.filter((e) => isSameDay(e.startDate, date)).length,
      });
    }
    return cells;
  }, [events, today]);

  const stats = useMemo(
    () => ({
      total: events.length,
      scheduled: events.filter((e) => e.status === 'scheduled').length,
      attendees: events.reduce((s, e) => s + (e.attendees?.length || 0), 0),
      highPriority: events.filter((e) => e.priority === 'high').length,
    }),
    [events]
  );

  /* --- Actions --- */
  const openCreate = () => { setEditingEvent(null); setModalOpen(true); };
  const openEdit = (event) => { setEditingEvent(event); setModalOpen(true); };

  const handleSubmit = async (payload) => {
    if (editingEvent?._id) {
      await updateEvent(editingEvent._id, payload);
      toast.success('Événement mis à jour');
    } else {
      await createEvent(payload);
      toast.success('Événement créé');
    }
  };

  const handleDelete = async (event) => {
    if (!window.confirm(`Supprimer « ${event.title} » ?`)) return;
    try {
      await deleteEvent(event._id);
      toast.success('Événement supprimé');
    } catch (err) {
      toast.error(err?.message || 'Suppression impossible');
    }
  };

  const handleComplete = async (event) => {
    try {
      await updateStatus(event._id, 'completed');
      toast.success('Événement marqué comme terminé');
    } catch (err) {
      toast.error(err?.message || 'Mise à jour impossible');
    }
  };

  const cardActions = { onEdit: openEdit, onDelete: handleDelete, onComplete: handleComplete };

  /* --- États de liste --- */
  const EmptyState = ({ label }) => (
    <div className="text-center py-12">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(var(--c-accent-green-rgb), 0.1)' }}>
        <CalendarIcon style={{ color: 'rgb(var(--c-accent-green-rgb))' }} size={32} />
      </div>
      <p className="text-ink font-semibold mb-1">{label}</p>
      <p className="text-label text-sm">Créez votre premier événement avec « Nouvel événement ».</p>
    </div>
  );

  const renderList = (list, emptyLabel) => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12 text-label">
          <Loader2 className="animate-spin mr-2" size={20} /> Chargement…
        </div>
      );
    }
    if (!list.length) return <EmptyState label={emptyLabel} />;
    return (
      <div className="space-y-3">
        {list.map((event, i) => (
          <EventCard key={event._id} event={event} index={i} {...cardActions} />
        ))}
      </div>
    );
  };

  return (
    <div>
      {/* En-tête de page */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs2 mb-1" style={{ color: 'var(--c-text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Planning
          </p>
          <h1 className="text-ink font-semibold" style={{ fontSize: '18px' }}>Calendrier</h1>
        </div>
        <button onClick={openCreate} className="btn-primary w-full sm:w-auto justify-center">
          <Plus size={14} />
          Nouvel événement
        </button>
      </div>

      {error && (
        <div className="mb-6 flex items-center gap-3 p-4 rounded-lg" style={{ background: 'rgba(224,84,84,0.08)', border: '0.5px solid rgba(224,84,84,0.3)' }}>
          <AlertCircle size={18} style={{ color: 'var(--c-error)', flexShrink: 0 }} />
          <span className="text-sm" style={{ color: 'var(--c-error)' }}>{error}</span>
        </div>
      )}

      {/* Métriques */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total événements', value: stats.total, icon: CalendarIcon },
          { label: 'Planifiés', value: stats.scheduled, icon: Activity },
          { label: 'Participants', value: stats.attendees, icon: Users },
          { label: 'Priorité haute', value: stats.highPriority, icon: Target },
        ].map((m, i) => {
          const Icon = m.icon;
          return (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * (i + 1) }}
              className="card-metric p-5"
            >
              <div className="w-9 h-9 rounded-full flex items-center justify-center mb-3"
                style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
                <Icon size={18} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} strokeWidth={1.5} />
              </div>
              <p className="text-label mb-1">{m.label}</p>
              <p className="font-metric text-ink" style={{ fontSize: '24px', fontWeight: 600, lineHeight: 1 }}>{m.value}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Sélecteur de vue */}
      <div className="card p-1.5 mb-6 flex gap-1 flex-wrap">
        {views.map((view, index) => {
          const Icon = view.icon;
          return (
            <motion.button
              key={view.id}
              onClick={() => setCurrentView(view.id)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all text-sm"
              style={currentView === view.id
                ? { background: 'rgb(var(--c-accent-green-rgb))', color: '#000' }
                : { color: 'var(--c-text-muted)' }
              }
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Icon size={16} />
              <span>{view.label}</span>
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {/* Vue Jour */}
        {currentView === 'jour' && (
          <motion.div key="jour" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
            <div className="card p-4 sm:p-6">
              <h3 className="text-ink font-semibold mb-5 flex items-center gap-2 capitalize">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
                  <CalendarIcon size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                </div>
                {formatLongDate(today)}
              </h3>
              {renderList(dayEvents, "Aucun événement aujourd'hui")}
            </div>
          </motion.div>
        )}

        {/* Vue Semaine */}
        {currentView === 'semaine' && (
          <motion.div key="semaine" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }} className="space-y-4">
            <div className="card p-4 sm:p-6">
              <h3 className="text-ink font-semibold mb-5 flex items-center gap-2 capitalize">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
                  <Target size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                </div>
                {formatMonthYear(today)}
              </h3>
              <div className="grid grid-cols-7 gap-1.5 sm:gap-3">
                {weekDays.map((date, index) => {
                  const isToday = isSameDay(date, today);
                  const count = events.filter((e) => isSameDay(e.startDate, date)).length;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ y: -2 }}
                      className="relative p-2 sm:p-3 rounded-xl text-center cursor-pointer transition-all"
                      style={isToday
                        ? { background: 'rgb(var(--c-accent-green-rgb))', color: '#000' }
                        : { background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)', color: 'var(--c-ink)' }
                      }
                    >
                      {count > 0 && (
                        <div className={`absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center ${isToday ? 'bg-black text-accent-green' : 'bg-accent-green text-black'}`}>
                          {count}
                        </div>
                      )}
                      <div className="text-xs font-medium mb-1 capitalize">
                        {new Intl.DateTimeFormat('fr-FR', { weekday: 'short' }).format(date)}
                      </div>
                      <div className="text-base sm:text-xl font-bold">{date.getDate()}</div>
                      {isToday && <div className="hidden sm:block text-xs font-semibold mt-1">Aujourd'hui</div>}
                    </motion.div>
                  );
                })}
              </div>
            </div>

            <div className="card p-4 sm:p-6">
              <h3 className="text-ink font-semibold mb-5 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
                  <Activity size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                </div>
                Événements de la semaine
              </h3>
              {renderList(weekEvents, 'Aucun événement cette semaine')}
            </div>
          </motion.div>
        )}

        {/* Vue Mois */}
        {currentView === 'mois' && (
          <motion.div key="mois" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
            <div className="card p-4 sm:p-6">
              <h3 className="text-ink font-semibold mb-5 flex items-center gap-2 capitalize">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
                  <Target size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                </div>
                {formatMonthYear(today)}
              </h3>
              <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-3">
                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
                  <div key={day} className="text-center text-xs font-semibold text-label p-1 sm:p-2">{day}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 sm:gap-2">
                {monthGrid.map((cell, index) => {
                  if (!cell) return <div key={`empty-${index}`} />;
                  return (
                    <motion.div
                      key={cell.day}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.01 }}
                      whileHover={{ y: -2 }}
                      className="relative p-2 sm:p-3 rounded-lg text-center cursor-pointer transition-all"
                      style={cell.isToday
                        ? { background: 'rgb(var(--c-accent-green-rgb))', color: '#000' }
                        : { background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)', color: 'var(--c-ink)' }
                      }
                    >
                      <div className="text-sm font-bold">{cell.day}</div>
                      {cell.count > 0 && (
                        <div className="mt-1.5 flex justify-center gap-1">
                          {Array.from({ length: Math.min(cell.count, 3) }).map((_, i) => (
                            <div key={i} className="w-1.5 h-1.5 bg-current rounded-full" />
                          ))}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {/* Vue Agenda */}
        {currentView === 'agenda' && (
          <motion.div key="agenda" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
            <div className="card p-4 sm:p-6">
              <h3 className="text-ink font-semibold mb-5 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
                  <List size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                </div>
                Liste des événements
              </h3>
              {renderList(events, 'Aucun événement')}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {modalOpen && (
          <EventModal
            event={editingEvent}
            onClose={() => setModalOpen(false)}
            onSubmit={handleSubmit}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
